import React, { useEffect, RefObject, useState } from 'react';
import NewGameState from '../../_types/NewGameState';
import {
  ViewContainer,
  RoundTab,
  RoundTabs,
  RoundsTabContainer,
  ScrollIndicator,
  ScrollButton,
  RoundsNavigationWrapper,
} from './Rounds.styles';
import { InputBodyContainer } from '../../NewGame.styles';
import Round from './Round';
import { GamifyToast } from 'application/common/CustomToasts';
// Import icons
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';

type Props = {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  tabsRef: RefObject<HTMLDivElement>;
  setShouldDisableNextOrFinishButton: (should: boolean) => void;
  scrollToTop?: () => void;
};

const RoundsView: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
  tabsRef,
  setShouldDisableNextOrFinishButton,
  scrollToTop,
}) => {
  const theme = useTheme();
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true); // Default to true

  // Check if scroll indicators should be shown
  const checkScrollIndicators = () => {
    if (!tabsRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;

    // Show left button if scrolled to the right
    setShowLeftScroll(scrollLeft > 5);

    // Show right button if not scrolled all the way to the right
    setShowRightScroll(scrollLeft + clientWidth < scrollWidth - 5);

    console.log('[SCROLL-DEBUG]', {
      scrollLeft,
      scrollWidth,
      clientWidth,
      showLeft: scrollLeft > 5,
      showRight: scrollLeft + clientWidth < scrollWidth - 5,
    });
  };

  // Scroll horizontally
  const scrollHorizontally = (direction: 'left' | 'right') => {
    if (!tabsRef.current) return;

    const scrollAmount = 150; // Adjust based on needs
    const newScrollLeft =
      tabsRef.current.scrollLeft +
      (direction === 'left' ? -scrollAmount : scrollAmount);

    tabsRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });

    // Update indicators after scrolling
    setTimeout(checkScrollIndicators, 300);
  };

  // Add scroll event listener
  useEffect(() => {
    const tabsElement = tabsRef.current;
    if (tabsElement) {
      tabsElement.addEventListener('scroll', checkScrollIndicators);

      // Initial check
      checkScrollIndicators();

      // Check after render to ensure accurate measurements
      setTimeout(checkScrollIndicators, 100);

      return () => {
        tabsElement.removeEventListener('scroll', checkScrollIndicators);
      };
    }
  }, []);

  // Check scroll indicators when rounds change
  useEffect(() => {
    if (newGameState.totalRounds > 0) {
      setTimeout(checkScrollIndicators, 100);
    }
  }, [newGameState.totalRounds]);

  // Scroll active tab into view
  useEffect(() => {
    if (tabsRef.current && newGameState.currentRound > 0) {
      const activeTab = tabsRef.current.querySelector('[data-active="true"]');
      if (activeTab) {
        const containerWidth = tabsRef.current.offsetWidth;
        const tabLeft = activeTab.getBoundingClientRect().left;
        const containerLeft = tabsRef.current.getBoundingClientRect().left;
        const scrollLeft =
          tabLeft -
          containerLeft -
          containerWidth / 2 +
          activeTab.clientWidth / 2;

        tabsRef.current.scrollTo({
          left: tabsRef.current.scrollLeft + scrollLeft,
          behavior: 'smooth',
        });

        // Check scroll indicators after scrolling
        setTimeout(checkScrollIndicators, 300);
      }
    }
  }, [newGameState.currentRound]);

  // Validate current round before allowing tab change
  const validateRoundBeforeChange = (targetRound: number) => {
    if (targetRound <= newGameState.currentRound) {
      // Allow going back to previous rounds without validation
      return true;
    }

    // Get current round data to validate
    const currentRoundIndex = newGameState.currentRound - 1;
    const currentRound = newGameState.bracket.roundValues[currentRoundIndex];

    console.log('[ROUND-TAB-VALIDATION] Validating before tab change', {
      currentRound: newGameState.currentRound,
      targetRound,
      currentRoundData: currentRound,
    });

    // Check required fields
    if (!currentRound?.bracketName) {
      GamifyToast.error(
        'Round name is required',
        'Please fill in the round name before proceeding'
      );
      return false;
    }

    if (!currentRound?.KPIRuleID) {
      GamifyToast.error(
        'KPI is required',
        'Please select a KPI before proceeding'
      );
      return false;
    }

    if (!currentRound?.startDate) {
      GamifyToast.error(
        'Start date is required',
        'Please select a start date before proceeding'
      );
      return false;
    }

    if (!currentRound?.endDate) {
      GamifyToast.error(
        'End date is required',
        'Please select an end date before proceeding'
      );
      return false;
    }

    return true;
  };

  if (newGameState.totalRounds <= 0) return null;

  return (
    <ViewContainer theme={theme.custom}>
      {/* Horizontal Round Tabs with Navigation */}
      <RoundsNavigationWrapper theme={theme.custom}>
        {/* Left scroll button */}
        <ScrollButton
          theme={theme.custom}
          visible={showLeftScroll}
          direction="left"
          onClick={() => scrollHorizontally('left')}
          aria-label="Scroll left"
        >
          <ChevronLeftIcon style={{ fontSize: '20px' }} />
        </ScrollButton>

        <RoundsTabContainer theme={theme.custom} ref={tabsRef}>
          <ScrollIndicator
            theme={theme.custom}
            visible={showLeftScroll}
            direction="left"
          />
          <RoundTabs>
            {Array.from({ length: newGameState.totalRounds }).map(
              (_, index) => (
                <RoundTab
                  key={index}
                  theme={theme.custom}
                  data-active={newGameState.currentRound === index + 1}
                  isActive={newGameState.currentRound === index + 1}
                  onClick={() => {
                    const targetRound = index + 1;
                    const isValid = validateRoundBeforeChange(targetRound);

                    if (isValid) {
                      onChangeNewGameState({
                        ...newGameState,
                        currentRound: targetRound,
                      });
                    }
                  }}
                >
                  Round {index + 1}
                </RoundTab>
              )
            )}
          </RoundTabs>
          <ScrollIndicator
            theme={theme.custom}
            visible={showRightScroll}
            direction="right"
          />
        </RoundsTabContainer>

        {/* Right scroll button */}
        <ScrollButton
          theme={theme.custom}
          visible={showRightScroll}
          direction="right"
          onClick={() => scrollHorizontally('right')}
          aria-label="Scroll right"
        >
          <ChevronRightIcon style={{ fontSize: '20px' }} />
        </ScrollButton>
      </RoundsNavigationWrapper>

      <InputBodyContainer theme={theme as any}>
        {Boolean(newGameState.totalRounds) &&
          newGameState.bracket.totalCompetitors > 0 &&
          Array.from({ length: newGameState.totalRounds }).map((_, index) => {
            return (
              index + 1 === newGameState.currentRound && (
                <Round
                  key={index}
                  tabNo={index + 1}
                  newGameState={newGameState}
                  onChangeNewGameState={onChangeNewGameState}
                  setShouldDisableNextOrFinishButton={
                    setShouldDisableNextOrFinishButton
                  }
                  scrollToTop={scrollToTop}
                />
              )
            );
          })}
      </InputBodyContainer>
    </ViewContainer>
  );
};

export default RoundsView;
